<template>
  <div class="data-management well-page ocr-page">
    <div class="ocr-shell">
      <aside class="ocr-sidebar">
        <OcrSidebarSkeleton v-if="bootstrapping" />
        <template v-else>
          <OcrFolderPanel
            sidebar
            :loading="folderLoading"
            :folders="folders"
            :base-name="baseName"
            :selected="selectedFolder"
            @select="onSelectFolder"
            @create="onCreateFolder"
            @delete="onDeleteFolder"
          />
          <OcrSubNav />
        </template>
      </aside>

      <div class="ocr-main-body">
        <OcrPageShimmer v-if="bootstrapping" :variant="layoutShimmerVariant" />
        <OcrApiErrorPanel
          v-else-if="sessionError"
          :message="sessionError"
          :retrying="retryingSession"
          @retry="bootstrap(true)"
        />
        <router-view v-else v-slot="{ Component }">
          <component :is="Component" @refresh-folders="loadFolders" />
        </router-view>
      </div>
    </div>

    <OcrSnackbar
      v-model="snackbar.show"
      :message="snackbar.text"
      :variant="snackbar.variant"
      :timeout="4000"
    />
  </div>
</template>

<script>
import OcrFolderPanel from "@/components/layout/OCR/OcrFolderPanel.vue";
import OcrSubNav from "@/components/layout/OCR/OcrSubNav.vue";
import OcrPageShimmer from "@/components/layout/OCR/OcrPageShimmer.vue";
import OcrApiErrorPanel from "@/components/layout/OCR/OcrApiErrorPanel.vue";
import OcrSnackbar from "@/components/layout/OCR/OcrSnackbar.vue";
import OcrSidebarSkeleton from "@/components/layout/OCR/OcrSidebarSkeleton.vue";
import ocrApi, { ensureOcrSession, formatOcrApiError } from "@/services/ocrApi";

export default {
  name: "OcrLayout",
  components: {
    OcrFolderPanel,
    OcrSubNav,
    OcrPageShimmer,
    OcrApiErrorPanel,
    OcrSnackbar,
    OcrSidebarSkeleton,
  },
  data() {
    return {
      bootstrapping: true,
      retryingSession: false,
      sessionError: null,
      folderLoading: true,
      folders: [],
      baseName: "Trial",
      selectedFolder: "Trial",
      contextKey: 0,
      snackbar: { show: false, text: "", variant: "info" },
    };
  },
  provide() {
    return {
      ocrContextKey: () => this.contextKey,
      ocrSelectedFolder: () => this.selectedFolder,
    };
  },
  computed: {
    layoutShimmerVariant() {
      const segment = this.$route.path.split("/").filter(Boolean).pop();
      const map = {
        dashboard: "dashboard",
        upload: "upload",
        reports: "reports",
        configuration: "configuration",
        "test-data": "test-data",
      };
      return map[segment] || "dashboard";
    },
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    async bootstrap(fromRetry = false) {
      this.sessionError = null;
      this.bootstrapping = true;
      this.retryingSession = fromRetry;
      try {
        await ensureOcrSession();
      } catch (e) {
        this.sessionError = formatOcrApiError(e, "Could not start OCR session.");
        this.notify(this.sessionError, "error");
        return;
      } finally {
        this.bootstrapping = false;
        this.retryingSession = false;
      }
      await this.loadFolders();
    },
    notify(text, variant = "success") {
      this.snackbar = { show: true, text, variant };
    },
    async loadFolders() {
      this.folderLoading = true;
      try {
        const { data } = await ocrApi.get("api/v1/folders");
        this.folders = data.folders || [];
        this.baseName = data.base_name || "Trial";
        this.selectedFolder = data.selected || this.baseName;
      } catch (e) {
        this.notify(formatOcrApiError(e, "Could not load folders."), "error");
      } finally {
        this.folderLoading = false;
      }
    },
    async onSelectFolder(name) {
      await ocrApi.put("api/v1/folders/selected", null, { params: { folder_name: name } });
      this.selectedFolder = name;
      this.contextKey += 1;
    },
    async onCreateFolder(name) {
      await ocrApi.post("api/v1/folders", { name });
      await this.loadFolders();
      this.contextKey += 1;
      this.notify(`Created folder ${name}`);
    },
    async onDeleteFolder(name) {
      await ocrApi.delete(`api/v1/folders/${encodeURIComponent(name)}`);
      await this.loadFolders();
      this.contextKey += 1;
      this.notify(`Deleted folder ${name}`);
    },
  },
};
</script>
